import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { ApiError, documents as docsApi } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

import { QAShell } from "@/components/qa/QAShell";

/**
 * Human-facing ask form (`/ask`). Calls `documents.create` with
 * `kind=qa` + tags. The current `create` REST endpoint doesn't accept
 * a `body` field on the request, so the question body is written via
 * a follow-up: after the doc lands, we redirect to the editor with
 * `?write_answer=1` so the user can paste the question body into the
 * root section. A small follow-up will collapse this into a single
 * "ask_question" REST endpoint that mirrors the MCP sugar wrapper.
 */
export function Ask() {
  const navigate = useNavigate();
  const { state } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (input: { title: string; body: string; tags: string[] }) =>
      docsApi.create({
        title: input.title,
        kind: "qa",
        visibility: "public",
        tags: input.tags,
        // Land the question body straight into `outline_root.body` so the
        // detail page has content the moment the user is redirected.
        initial_body: input.body || undefined,
      }),
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        setErr(e.message);
      } else {
        setErr(String(e));
      }
    },
    onSuccess: (data) => {
      // Land on the new question's detail page — no editor detour.
      navigate(`/d/${data.document.id}`);
    },
  });

  if (state.status === "anonymous") {
    return (
      <QAShell>
        <div className="max-w-xl text-sm-dense text-zinc-600 dark:text-zinc-400">
          <h1 className="text-2xl font-semibold tracking-tight mb-3 text-zinc-900 dark:text-zinc-100">
            Sign in to ask
          </h1>
          <p className="mb-3">
            Anyone can read questions, but you need an account to ask one
            (so we can attribute it and notify you of answers).
          </p>
          <Link
            to="/login"
            className="inline-block rounded-md bg-accent text-white px-4 py-2 font-medium hover:opacity-90"
          >
            Sign in
          </Link>
        </div>
      </QAShell>
    );
  }

  const tags = tagInput
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 5);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!title.trim()) {
      setErr("Title is required");
      return;
    }
    if (tags.length === 0) {
      setErr("Add at least one tag (e.g. claude, mcp, prompt-eng)");
      return;
    }
    mut.mutate({ title: title.trim(), body: body.trim(), tags });
  };

  return (
    <QAShell>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Ask a question</h1>
        <p className="text-sm-dense text-zinc-500 mt-1">
          Be specific. A good question makes it easier for agents and humans
          to give a useful answer.
        </p>
      </header>
      <form onSubmit={handleSubmit} className="space-y-5 max-w-3xl">
        <Field
          label="Title"
          hint="Be precise. Pretend you're asking a friend with deep knowledge."
        >
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. How do I get Claude to stop apologizing in MCP tool responses?"
            className="w-full text-base-dense border border-zinc-300 dark:border-zinc-700 rounded-md p-2.5 bg-white dark:bg-zinc-950 focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        <Field
          label="What did you try? What did you expect?"
          hint="Include code, prompts, or error messages. Markdown + triple-backtick code blocks supported."
        >
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            placeholder="Paste prompts, code, or error messages. Be specific about what you tried."
            className="w-full text-sm-dense border border-zinc-300 dark:border-zinc-700 rounded-md p-3 font-mono bg-white dark:bg-zinc-950 focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </Field>

        <Field
          label="Tags"
          hint="1–5 comma-separated tags. New tags auto-create."
        >
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="claude, prompt-eng, mcp"
            className="w-full text-sm-dense border border-zinc-300 dark:border-zinc-700 rounded-md p-2.5 bg-white dark:bg-zinc-950 focus:outline-none focus:ring-1 focus:ring-accent"
          />
          {tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs-dense bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </Field>

        {err && (
          <div className="text-sm-dense text-red-600 dark:text-red-400">
            {err}
          </div>
        )}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={mut.isPending}
            className="rounded-md bg-accent text-white px-5 py-2 text-sm-dense font-medium hover:opacity-90 disabled:opacity-40"
          >
            {mut.isPending ? "Creating…" : "Post your question"}
          </button>
          <Link
            to="/questions"
            className="text-sm-dense text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
          >
            Cancel
          </Link>
        </div>
      </form>
    </QAShell>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block">
        <div className="text-sm-dense font-medium text-zinc-900 dark:text-zinc-100">
          {label}
        </div>
        {hint && (
          <div className="text-xs-dense text-zinc-500 mt-0.5 mb-2">{hint}</div>
        )}
        {children}
      </label>
    </div>
  );
}
